import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { errorText } from "@common/api";
import { admin, queryKeys, type Banner, type BannerLevel } from "../../api/salvagers";

const EMPTY: Banner = { text: "", level: "info", enabled: true };

/** Set, disable or clear the message every visitor sees under the header. */
export default function BannerEditor() {
  const queryClient = useQueryClient();
  const stored = useQuery({ queryKey: queryKeys.adminBanner, queryFn: admin.banner });
  // Edits live here until saved; otherwise the form shows what is stored.
  const [draft, setDraft] = useState<Banner | null>(null);
  const banner = draft ?? stored.data?.banner ?? EMPTY;

  const saved = () => {
    setDraft(null);
    queryClient.invalidateQueries({ queryKey: queryKeys.adminBanner });
    queryClient.invalidateQueries({ queryKey: queryKeys.banner });
    queryClient.invalidateQueries({ queryKey: queryKeys.tables });
  };
  const save = useMutation({ mutationFn: admin.setBanner, onSuccess: saved });
  const clear = useMutation({ mutationFn: admin.clearBanner, onSuccess: saved });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate({ ...banner, text: banner.text.trim() });
  };
  const error = save.error ?? clear.error ?? stored.error;

  return (
    <section>
      <h2>Site banner</h2>
      <p className="muted">Shown to every visitor under the header while enabled.</p>
      <form onSubmit={submit} className="stack">
        <textarea
          value={banner.text}
          onChange={(event) => setDraft({ ...banner, text: event.target.value })}
          placeholder="Banner text"
          maxLength={300}
          rows={2}
          aria-label="Banner text"
        />
        <div className="row">
          <select
            value={banner.level}
            onChange={(event) => setDraft({ ...banner, level: event.target.value as BannerLevel })}
            aria-label="Banner level"
          >
            <option value="info">Info</option>
            <option value="warning">Warning</option>
          </select>
          <label className="check">
            <input
              type="checkbox"
              checked={banner.enabled}
              onChange={(event) => setDraft({ ...banner, enabled: event.target.checked })}
            />
            Enabled
          </label>
          <button className="btn primary" type="submit" disabled={save.isPending || !banner.text.trim()}>
            Save
          </button>
          <button
            className="btn"
            type="button"
            onClick={() => clear.mutate()}
            disabled={clear.isPending || !stored.data?.banner}
          >
            Clear
          </button>
        </div>
      </form>
      {stored.data?.updated_by && (
        <p className="muted small">
          Last set by {stored.data.updated_by}
          {stored.data.updated_at ? ` on ${new Date(stored.data.updated_at * 1000).toLocaleString()}` : ""}
        </p>
      )}
      {error && <p className="notice error">{errorText(error, "That didn't work.")}</p>}
    </section>
  );
}
